/**
 * SOS — danger-toned emergency alert. Hold the ring button for ~3s to send;
 * releasing early cancels. Wired: POST /api/services/sos, quick-dial rows
 * from GET /api/account/emergency-contacts. Port of SOSScreen in
 * design/source/screens-misc.jsx (R25).
 */
import { router } from 'expo-router';
import React from 'react';
import { Animated, Pressable, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { glow, space } from '@bero/tokens';
import { bool, num, str, useGateList } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { callNumber, CallIconButton, IconCircle } from '@/features/account-admin/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, BrandLockup, Button, Card, Chip, Skeleton } from '@/ui';

const HOLD_MS = 3000;
const RING_SIZE = 200;

type Contact = { id: string; name: string; rel: string; phone?: string; primary: boolean };

function toContact(c: unknown, i: number): Contact {
  return {
    id: str(c, 'id') ?? `e${i}`,
    name: str(c, 'label', 'name', 'fullName') ?? 'Contact',
    rel: str(c, 'rel', 'relation', 'relationship') ?? '',
    phone: str(c, 'phone', 'phoneNumber'),
    primary: bool(c, 'primary', 'isPrimary') ?? false,
  };
}

const EMERGENCY_TYPES = [
  ['Medical', 'sos_type_medical'], ['Fire', 'sos_type_fire'],
  ['Security', 'sos_type_security'], ['Other', 'sos_type_other'],
] as const;

const SEND_LIST = [
  { icon: 'shield', key: 'sos_send_guards' },
  { icon: 'users', key: 'sos_send_committee' },
  { icon: 'phone', key: 'sos_send_contact' },
  { icon: 'map-pin', key: 'sos_send_flatinfo' },
] as const;

export default function SOSScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state: contactsState } = useGateList<unknown>('/api/account/emergency-contacts', 'contacts', 'emergency');
  const contacts = items.map(toContact);

  const [type, setType] = React.useState('Security');
  const [phase, setPhase] = React.useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = React.useState<string | null>(null);
  const [etaSeconds, setEtaSeconds] = React.useState(120);

  const [progress] = React.useState(() => new Animated.Value(0));

  const cancelHold = React.useCallback(() => {
    progress.stopAnimation();
    // useNativeDriver: false — the fill bar animates `height`, a layout prop
    // the native driver can't touch (only transform/opacity qualify).
    Animated.timing(progress, { toValue: 0, duration: 180, useNativeDriver: false }).start();
  }, [progress]);

  const sendSOS = React.useCallback(async () => {
    setPhase('sending');
    setError(null);
    try {
      const body = await api.post<unknown>('/api/services/sos', { type });
      setEtaSeconds(num(body, 'etaSeconds', 'eta') ?? 120);
      setPhase('sent');
    } catch {
      setError(t('sos_error'));
      setPhase('idle');
      progress.setValue(0);
    }
  }, [progress, type, t]);

  const beginHold = () => {
    if (phase !== 'idle') return;
    Animated.timing(progress, { toValue: 1, duration: HOLD_MS, useNativeDriver: false }).start(({ finished }) => {
      if (finished) void sendSOS();
    });
  };

  const endHold = () => {
    if (phase === 'idle') cancelHold();
  };

  // ETA countdown, ticking every second once sent.
  React.useEffect(() => {
    if (phase !== 'sent') return;
    const t = setInterval(() => setEtaSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const mm = Math.floor(etaSeconds / 60);
  const ss = etaSeconds % 60;

  if (phase === 'sent') {
    return (
      <SafeAreaView edges={['top', 'bottom']} style={{ flex: 1, backgroundColor: colors.canvas0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, paddingHorizontal: space.s4, paddingTop: space.s2 }}>
          <BrandLockup markHeight={20} />
          <AppText variant="bodyLg" weight="500">{t('emergency')}</AppText>
        </View>
        <View style={{ flex: 1, alignItems: 'center', paddingTop: space.s10, paddingHorizontal: space.s6 }}>
          <Icon name="check-circle" size={64} color={colors.success500} />
          <AppText variant="headingLg" style={{ marginTop: space.s6, textAlign: 'center' }}>{t('sos_sent')}</AppText>
          <AppText variant="bodyLg" tone="secondary" style={{ marginTop: space.s2, textAlign: 'center' }}>
            {t('sos_on_way')}
          </AppText>
          <AppText variant="displayLg" mono style={{ marginTop: space.s6 }}>
            ETA {mm}:{String(ss).padStart(2, '0')}
          </AppText>
          <Button
            title={t('cancel_sos')}
            kind="destructive"
            size="lg"
            style={{ marginTop: space.s10, alignSelf: 'stretch' }}
            onPress={() => router.back()}
          />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView edges={['top', 'bottom']} style={{ flex: 1, backgroundColor: colors.danger50 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, paddingHorizontal: space.s4, paddingTop: space.s2 }}>
        <BrandLockup markHeight={20} />
        <AppText variant="bodyLg" weight="500" style={{ flex: 1 }}>{t('emergency')}</AppText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={() => router.back()}
          hitSlop={8}
          style={{ width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.danger500 }}
        >
          <Icon name="x" size={16} color={colors.danger700} />
        </Pressable>
      </View>
      <ScrollView contentContainerStyle={{ alignItems: 'center', paddingTop: space.s6, paddingHorizontal: space.s6, paddingBottom: space.s8 }} showsVerticalScrollIndicator={false}>
        <Icon name="alert-triangle" size={56} color={colors.danger500} />
        <AppText variant="headingLg" weight="600" style={{ marginTop: space.s4, color: colors.danger700, textAlign: 'center' }}>
          {t('sos_alert')}
        </AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s3, textAlign: 'center', maxWidth: 300 }}>
          {t('sos_hold_instruction')}
        </AppText>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginTop: space.s4, justifyContent: 'center' }}>
          {EMERGENCY_TYPES.map(([val, key]) => (
            <Chip key={val} label={t(key)} active={type === val} onPress={() => phase === 'idle' && setType(val)} />
          ))}
        </View>

        <HoldRing progress={progress} size={RING_SIZE} onPressIn={beginHold} onPressOut={endHold} disabled={phase !== 'idle'} />

        {error ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3, textAlign: 'center' }}>{error}</AppText> : null}

        <Button
          title={t('call_112')}
          kind="secondary"
          style={{ marginTop: space.s6 }}
          onPress={() => callNumber('112')}
        />

        <View style={{ marginTop: space.s8, alignSelf: 'stretch', gap: space.s3 }}>
          <AppText variant="bodySm" tone="tertiary">{t('sos_when_send')}</AppText>
          {SEND_LIST.map((row) => (
            <View key={row.key} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
              <Icon name={row.icon} size={18} color={colors.danger500} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>{t(row.key)}</AppText>
            </View>
          ))}
        </View>

        {contactsState === 'loading' ? (
          <View style={{ marginTop: space.s6, alignSelf: 'stretch', gap: space.s3 }}>
            <AppText variant="bodySm" tone="tertiary">{t('quick_dial')}</AppText>
            {[0, 1].map((i) => <Skeleton key={i} h={64} />)}
          </View>
        ) : contacts.length > 0 ? (
          <View style={{ marginTop: space.s6, alignSelf: 'stretch', gap: space.s3 }}>
            <AppText variant="bodySm" tone="tertiary">{t('quick_dial')}</AppText>
            {contacts.slice(0, 3).map((c) => (
              <Card key={c.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                <IconCircle name="phone" size={40} bg={colors.danger50} fg={colors.danger500} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="500">{c.name}</AppText>
                  {c.rel ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{c.rel}</AppText> : null}
                </View>
                <CallIconButton phone={c.phone} name={c.name} prominent />
              </Card>
            ))}
          </View>
        ) : null}

        <Button
          title={t('cancel')}
          kind="tertiary"
          style={{ marginTop: space.s6 }}
          onPress={() => router.back()}
        />
      </ScrollView>
    </SafeAreaView>
  );
}

/** Hold-fill ring: a bottom-up fill bar clipped by the circular button — reads
 * reliably on react-native-web, unlike the two-half conic-rotate hack (P1-2:
 * that trick doesn't visibly clip under RN-web's CSS output). */
function HoldRing({ progress, size, onPressIn, onPressOut, disabled }: {
  progress: Animated.Value; size: number; onPressIn: () => void; onPressOut: () => void; disabled?: boolean;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  const half = size / 2;
  const fillHeight = progress.interpolate({ inputRange: [0, 1], outputRange: [0, size] });

  return (
    <View style={{
      width: size + 8, height: size + 8, borderRadius: (size + 8) / 2,
      backgroundColor: colors.danger700, alignItems: 'center', justifyContent: 'center', marginTop: space.s10,
      ...glow(colors.danger500, 0.35, 22),
    }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Hold to send SOS"
        disabled={disabled}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        style={{ width: size, height: size, borderRadius: half, overflow: 'hidden', backgroundColor: colors.danger500 }}
      >
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height: fillHeight,
            backgroundColor: colors.danger700,
          }}
        />
        <View
          pointerEvents="none"
          style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}
        >
          <AppText variant="headingSm" weight="600" style={{ color: colors.textOnDark, letterSpacing: 1 }}>{t('hold')}</AppText>
        </View>
      </Pressable>
    </View>
  );
}
